import React, { useState, useEffect } from "react";
import { connect } from "react-redux";

import { loadPageData } from "redux/actions/Request";

import { Alert } from "@material-ui/lab";

import { isNil } from "lodash";

import {
  Dialog,
  Button,
  Divider,
  MenuItem,
  TextField,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Tooltip,
} from "@material-ui/core";

import { LCGroup } from "lib/models/LCGroup";

import CloseIcon from "@material-ui/icons/Close";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import RemoveRedEye from "@material-ui/icons/RemoveRedEye";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import DeleteIcon from "@material-ui/icons/Delete";

import SimpleTable from "views/Components/SimpleTable";
import LicenseRestriction from "views/Components/License";

import {
  sendEmailToSalesTeam,
  setLogShippingFeature,
  getInstallationScript,
  getInstallationTableColumns,
} from "./utils";

import { installationPageStyles } from "./installationPageStyles";

import { copyTextToClipboard, isOrganizationChildOfNFR } from "utils";

import { ActivationConfirmationDialog } from "views/Components/BlumiraAgent/ActivationConfirmationDialog";
import { BlumiraAgentActivationBanner } from "views/Components/BlumiraAgent/BlumiraAgentActivationBanner";
import { SeatCountInfoCard } from "views/Components/BlumiraAgent/SeatCountInfoCard";

export const defaultKeyValue = "default";

export const isCopyButtonDisabled = (selectedKey, defaultKey) => {
  // if an invalid key is passed, disable
  if (!selectedKey) return true;

  // if the user has not selected a key we can disable
  // the copy button since there's no script to copy
  return selectedKey === defaultKey;
};

const InstallationPageView = (props) => {
  const classes = installationPageStyles();

  const [isSaving, setIsSaving] = useState(false);
  const [isKeyMasked, setIsKeyMasked] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedGroupKey, setSelectedGroupKey] = useState("");
  const [selectedGroupModel, setSelectedGroupModel] = useState({});
  const [installationScript, setInstallationScript] = useState("");
  const [isActivatingAgent, setIsActivatingAgent] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [wasEmailSuccessful, setWasEmailSuccessful] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(defaultKeyValue);
  const [isRemoveKeyDialogOpen, setIsRemoveKeyDialogOpen] = useState(false);
  const [selectedGroupDescription, setSelectedGroupDescription] = useState("");
  const [selectedInstallationKey, setSelectedInstallationKey] =
    useState(defaultKeyValue);
  const [selectedAgentSeatCount, setSelectedAgentSeatCount] = useState(0);
  const [
    isActivationConfirmationDialogOpen,
    setIsActivationConfirmationDialogOpen,
  ] = useState(false);
  const [orgTotalGroupSeatCount, setOrgTotalGroupSeatCount] = useState(0);
  const [orgGroupDeviceLeftOverCount, setOrgGroupDeviceLeftOverCount] =
    useState(0);
  const [orgGroupInstalledDeviceCount, setOrgGroupInstalledDeviceCount] =
    useState(0);
  const [isOrgMaxAgentsReached, setIsOrgMaxAgentsReached] = useState(false);
  const [errorHelperText, setErrorHelperText] = useState(false);

  useEffect(() => {
    // get key and description from selected
    // group model to update in dialog
    const {
      description = "",
      installationKey = "",
      agentSeatCount = "",
    } = selectedGroupModel;

    setSelectedGroupKey(installationKey);
    setSelectedGroupDescription(description);
    setSelectedAgentSeatCount(agentSeatCount);
  }, [selectedGroupModel]);

  useEffect(() => {
    if (props.ready) {
      const total = getOrgGroupsSeatCount(props.keys);
      // Find max # of agents that can be added to a group seat count before reaching the org seat count limit
      if (selectedGroupModel.id) {
        setOrgTotalGroupSeatCount(
          props.currentOrg?.config?.agent_seat_count -
            total +
            selectedGroupModel.agentSeatCount
        );
      } else {
        setOrgTotalGroupSeatCount(
          props.currentOrg?.config?.agent_seat_count - total
        );
      }
      // Find # of agents an org has left over before reaching their org seat count limit
      setOrgGroupDeviceLeftOverCount(
        props.currentOrg?.config?.agent_seat_count - total
      );

      if (total === props.currentOrg?.config?.agent_seat_count) {
        setIsOrgMaxAgentsReached(true);
      } else {
        setIsOrgMaxAgentsReached(false);
      }
    }
  }, [
    props.ready,
    props.currentOrg?.config?.agent_seat_count,
    selectedGroupModel.agentSeatCount,
    props.keys,
  ]);

  useEffect(() => {
    if (props.ready) {
      const total = getOrgGroupsInstalledDevicesCount(props.keys);
      // Find # of agents an org has currently installed across all groups
      setOrgGroupInstalledDeviceCount(total);
    }
  }, [props.ready, props.keys]);

  useEffect(() => {
    setInstallationScript("");
  }, [props.orgId]);

  const getOrgGroupsSeatCount = (keys) => {
    const totalGroupSeatArray = keys.map((key) => key.agentSeatCount);

    const totalCount = totalGroupSeatArray.reduce(
      (accumulator, currentValue) => accumulator + currentValue,
      0
    );
    return totalCount;
  };

  const getOrgGroupsInstalledDevicesCount = (keys) => {
    const totalGroupDeviceArray = keys.map((key) => key.agentCount);

    const totalCount = totalGroupDeviceArray.reduce(
      (accumulator, currentValue) => accumulator + currentValue,
      0
    );
    return totalCount;
  };

  const handleEditTableRowClick = async (model) => {
    // get key from selected model
    const activeInstallationKey = await handleGetInstallationKey(model);

    // get script command to display in detail dialog
    const script = getInstallationScript(activeInstallationKey);

    setInstallationScript(script);
    setSelectedGroupModel(model);
    setIsDetailsModalOpen(true);
  };

  const handleOpenKeyDialog = () => {
    const newGropModel = new LCGroup();
    setSelectedGroupModel(newGropModel);
    setIsDetailsModalOpen(true);
  };

  const handleCopyKeyValue = (event, model) => {
    // if we have a model defined, this is
    // happening from the row action item
    const keyToCopy = model ? model.key : selectedGroupKey;
    copyTextToClipboard(keyToCopy);
  };

  const handleGetInstallationKey = async (model) => {
    let installationKeyToReturn;

    if (model.installationKey) {
      installationKeyToReturn = model.installationKey;
    } else {
      // we need to make a request to get the installation key here
      await new LCGroup({ id: model.id }).read().then((newModel) => {
        installationKeyToReturn = newModel.installationKey;

        // set it on the model for future use
        model.set({ installationKey: newModel.installationKey });
      });
    }

    return installationKeyToReturn;
  };

  const handleCopyInstallationScript = async (contextMenu = {}) => {
    const { model } = contextMenu;

    if (model) {
      // user is copying from context menu and we
      // need to generate the installation command

      const installationKeyToUse = await handleGetInstallationKey(model);

      const scriptToCopy = getInstallationScript(installationKeyToUse);
      copyTextToClipboard(scriptToCopy);
    } else {
      // we are copying from  the wizard and have
      // the installation script in memory
      copyTextToClipboard(installationScript);
    }
  };

  const handleCloseModal = () => {
    setErrorMessage("");
    setInstallationScript("");
    setIsDetailsModalOpen(false);
    setSelectedGroupId(defaultKeyValue);
    setSelectedInstallationKey(defaultKeyValue);
  };

  const handleSubmit = () => {
    setIsSaving(true);
    // get proper action based on id presence
    const action = selectedGroupModel.id ? "update" : "create";

    // set description and agentSeatCount of group accordingly
    selectedGroupModel.set({
      description: selectedGroupDescription,
      agentSeatCount: selectedAgentSeatCount,
    });

    selectedGroupModel[action]()
      .then(async (newAgentModel) => {
        // await here so we have all keys in our props to avoid
        // material warnings that this new value is out of range
        await props.reload(true);

        handleCloseModal();
        setErrorMessage("");
        setErrorHelperText("");
        setIsSaving(false);

        // after we create, generate an installation script and
        // populate the UI for quick access
        if (action === "create") {
          setSelectedGroupId(newAgentModel.id);
          const installationKeyToUse = await handleGetInstallationKey(
            newAgentModel
          );
          setSelectedInstallationKey(installationKeyToUse);
          const script = getInstallationScript(installationKeyToUse);
          setInstallationScript(script);
        }
      })
      .catch((e) => {
        setErrorMessage("Oops, we had a problem saving that installation key.");
        setIsSaving(false);

        if (
          !selectedGroupModel.agentSeatCount ||
          selectedGroupModel.agentSeatCount > orgGroupDeviceLeftOverCount
        ) {
          setErrorHelperText(
            `Please enter a number between 1 and your available device limit of ${orgGroupDeviceLeftOverCount}`
          );
        }
      });
  };

  const handleChangeInstallationKey = async (event) => {
    const selectedGroupModelId = event.target.value;

    setSelectedGroupId(selectedGroupModelId);

    // get full model to determine if key is available
    const fullSelectedGroupModel = props.keys.find(
      ({ id }) => id === selectedGroupModelId
    );

    // if we find a model matching the selected id
    // we can use the model to retrieve an installation key
    if (fullSelectedGroupModel) {
      const installationKeyToUse = await handleGetInstallationKey(
        fullSelectedGroupModel
      );
      setSelectedInstallationKey(installationKeyToUse);
      const script = getInstallationScript(installationKeyToUse);
      setInstallationScript(script);
    } else {
      // if we do not find a model, revert to default
      setSelectedInstallationKey(defaultKeyValue);
      setSelectedGroupId(defaultKeyValue);
      setInstallationScript("");
    }
  };

  const handleDescriptionChange = (event) => {
    let newDescription = event.target.value;
    setSelectedGroupDescription(newDescription);
  };

  const toggleRemoveKeyDialog = () => {
    setIsRemoveKeyDialogOpen(!isRemoveKeyDialogOpen);
  };

  const handleRemoveKey = () => {
    // TODO: Need to look into error handling for certain situations here
    // i.e - if you delete a group that has agents in it the service will return an error
    selectedGroupModel
      .delete()
      .then(() => {
        props.reload(true);
        setIsDetailsModalOpen(false);
        toggleRemoveKeyDialog();
        setErrorMessage("");
        setInstallationScript("");
        setSelectedGroupId(defaultKeyValue);
      })
      .catch((err) => {
        const { message: detailedErrorMessage } = err;
        toggleRemoveKeyDialog();
        setErrorMessage(
          `Oops, something went wrong deleting your key: ${detailedErrorMessage}`
        );
      });
  };

  const handleActivateDevicesFeature = async () => {
    const { isOrgAChildOrg, isOrgAParentOrg, currentOrg, currentUser } = props;

    try {
      if (isOrgAChildOrg || isOrgAParentOrg) {
        setIsActivatingAgent(true);
        await setLogShippingFeature(currentOrg, currentUser.email);
        setIsActivatingAgent(false);

        // reload page for license feature updates
        window.location.reload();
      } else {
        try {
          await sendEmailToSalesTeam(currentOrg.name);
          setWasEmailSuccessful(true);
        } catch (e) {
          setWasEmailSuccessful(false);
        }
      }
    } catch (e) {
      // if anything goes wrong, reset
      setIsActivatingAgent(false);
    }
  };

  const toggleActivationConfirmationDialog = () => {
    setWasEmailSuccessful(false);
    setIsActivationConfirmationDialogOpen(!isActivationConfirmationDialogOpen);
  };

  const handleActivateClick = () => {
    const { isOrgAChildOrg, isOrgAParentOrg } = props;

    // allow MSP NFR admins to freely grant access to
    // this feature, else present confirmation dialog
    if (isOrgAChildOrg || isOrgAParentOrg) {
      handleActivateDevicesFeature();
    } else {
      toggleActivationConfirmationDialog();
    }
  };

  // TODO: repurpose for upsale
  const renderDisallow = () => {
    return;
  };

  const renderAllow = (children) => {
    return children;
  };

  const renderBanner = (children) => {
    return children;
  };

  const hideBanner = () => {
    return null;
  };

  const handleAgentSeatCountChange = (event) => {
    let newAgentSeatCount = event.target.value;
    setSelectedAgentSeatCount(newAgentSeatCount);
  };

  const hasGroupSeatCountBeenMet = ({ model = {} }) => {
    return model.agentCount === model.agentSeatCount ? true : false;
  };

  const renderInstallKeyField = () => {
    if (selectedGroupModel.agentCount === selectedGroupModel.agentSeatCount) {
      return (
        <Tooltip
          title={
            "Unable to copy installation script.  Device limit reached for this key."
          }
          placement={"top"}
        >
          <div>
            <TextField
              fullWidth={true}
              value={installationScript}
              className={classes.textField}
              label={"Agent install script"}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position={"end"}>
                    <IconButton
                      onClick={handleCopyInstallationScript}
                      disabled={true}
                      className={classes.inputIcons}
                    >
                      <FileCopyOutlinedIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              disabled={true}
            />
          </div>
        </Tooltip>
      );
    } else {
      return (
        <TextField
          value={installationScript}
          className={classes.textField}
          label={"Agent install script"}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position={"end"}>
                <IconButton
                  onClick={handleCopyInstallationScript}
                  className={classes.inputIcons}
                >
                  <FileCopyOutlinedIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      );
    }
  };

  const findProgressValue = () => {
    return Math.round(
      (orgGroupInstalledDeviceCount /
        props.currentOrg?.config?.agent_seat_count) *
        100
    );
  };

  // TODO: Verify if we are wanting this link to just open an email for them to contact the necessary team or if this needs to send an automated email.
  const requestMoreEmail =
    props.currentOrg?.market === 20
      ? "mailto:msp@blumira.com"
      : "mailto:sales-direct-ae@blumira.com";

  return (
    <div>
      {/*show error here only when dialog is closed, else it is handled in dialog */}
      {errorMessage && !isDetailsModalOpen && (
        <Alert severity="error">{errorMessage}</Alert>
      )}
      <LicenseRestriction
        requires="lcagent.create"
        renderAllow={hideBanner}
        renderDisallow={renderBanner}
      >
        <BlumiraAgentActivationBanner
          isOrgAParentOrg={props.isOrgAParentOrg}
          isOrgAChildOrg={props.isOrgAChildOrg}
          isActivatingAgent={isActivatingAgent}
          onActivateClick={handleActivateClick}
        />
      </LicenseRestriction>
      <LicenseRestriction
        requires="lcagent.create"
        renderAllow={renderAllow}
        renderDisallow={renderDisallow}
      >
        <div style={{ display: "flex" }}>
          <SeatCountInfoCard
            header={"Total number of allowed devices in your Blumira contract:"}
            deviceCount={props.currentOrg?.config?.agent_seat_count}
            link={true}
            linkUrl={requestMoreEmail}
          />
          <SeatCountInfoCard
            header={"Installed devices:"}
            deviceCount={orgGroupInstalledDeviceCount}
            progressBar={true}
            progressValue={findProgressValue()}
          />
        </div>
        <div className={classes.agentSetupStepsContainer}>
          <p className={classes.title}>Generate agent installation script</p>
          <div className={classes.agentSetupStepContainer}>
            <div className={classes.circularNumberContainer}>
              <p>1</p>
            </div>
            <div className={classes.stepTextInnerContainer}>
              <TextField
                select
                value={selectedGroupId}
                labelid={"selectInstallationKey"}
                onChange={handleChangeInstallationKey}
                className={classes.installationKeySelect}
              >
                <MenuItem value={defaultKeyValue}>
                  Select installation key
                </MenuItem>
                <MenuItem onClick={handleOpenKeyDialog}>
                  Create new installation key
                </MenuItem>
                <Divider />
                {props.keys.map((groupObj, idx) =>
                  groupObj.agentCount === groupObj.agentSeatCount ? (
                    <Tooltip
                      key={idx}
                      title={
                        "This group has reached its agent seat count limit."
                      }
                      placement={"right"}
                      className={classes.tooltip}
                    >
                      <div>
                        <MenuItem
                          key={groupObj.id}
                          value={groupObj.id}
                          disabled
                        >
                          {groupObj.description}
                        </MenuItem>
                      </div>
                    </Tooltip>
                  ) : (
                    <MenuItem key={groupObj.id} value={groupObj.id}>
                      {groupObj.description}
                    </MenuItem>
                  )
                )}
              </TextField>
            </div>
          </div>
          <div className={classes.agentSetupStepContainer}>
            <div className={classes.circularNumberContainer}>
              <p>2</p>
            </div>
            <div className={classes.stepTextOuterContainer}>
              <div className={classes.stepTextInnerContainer}>
                <p>
                  Copy custom installation script.
                  {/* 
                    FYI: hiding this link until GA
                    <a 
                      target={'_blank'}
                      rel={'noopener noreferrer'}
                      href={blumiraAgentSetupUrl}
                    >
                      Blumira Agent setup guide <OpenInNewIcon/>
                    </a> 
                  */}
                </p>
              </div>
              <TextField
                value={installationScript}
                className={classes.textField}
                inputProps={{ readOnly: true }}
              />
              <Button
                variant={"outlined"}
                className={classes.copyButton}
                onClick={handleCopyInstallationScript}
                disabled={isCopyButtonDisabled(
                  selectedInstallationKey,
                  defaultKeyValue
                )}
              >
                Copy
              </Button>
            </div>
          </div>
        </div>
        <SimpleTable
          isNorthStar
          data={props.keys}
          isFetching={!props.ready}
          title={"Installation Keys"}
          columns={getInstallationTableColumns(
            orgGroupInstalledDeviceCount,
            props.currentOrg?.config?.agent_seat_count
          )}
          emptyText={<span>No keys have been added yet</span>}
          titleTooltip={
            <span>
              Create one installation key for your entire organization or
              segment your devices into installation groups using unique keys.
              Deleting an installation key will unenroll only those devices that
              use that key.
            </span>
          }
          toolbarActions={[
            {
              icon: AddCircleIcon,
              onClick: handleOpenKeyDialog,
              tooltip: "Add installation key",
              disabled: isOrgMaxAgentsReached,
            },
          ]}
          contextMenuItems={[
            {
              text: "Installation key details",
              onClick: ({ model }) => handleEditTableRowClick(model),
            },
            {
              text: "Copy agent install script",
              onClick: handleCopyInstallationScript,
              isDisabled: hasGroupSeatCountBeenMet,
              disabledTooltip:
                "This group has reached its agent seat count limit.",
            },
          ]}
        />
      </LicenseRestriction>
      <Dialog
        open={isDetailsModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth={true}
      >
        <div className={classes.titleContainer}>
          <DialogTitle>Installation key details</DialogTitle>
          <IconButton color={"default"} onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent className={classes.dialogContent}>
          {errorMessage && (
            <Alert severity="error" className={classes.dialogAlert}>
              {errorMessage}
            </Alert>
          )}
          <TextField
            variant={"standard"}
            label={"Description"}
            style={{ marginBottom: 20 }}
            value={selectedGroupDescription}
            onChange={handleDescriptionChange}
          />
          <TextField
            variant={"standard"}
            label={"Device limit for this key"}
            style={{ marginBottom: 20 }}
            value={selectedAgentSeatCount}
            type={"number"}
            onChange={handleAgentSeatCountChange}
            inputProps={{
              min: selectedGroupModel.id ? selectedGroupModel.agentCount : 1,
              max: orgTotalGroupSeatCount,
            }}
            helperText={
              errorHelperText.length
                ? errorHelperText
                : `* Required (minimum of 1) - ${orgGroupDeviceLeftOverCount} more available`
            }
            error={errorHelperText.length ? true : false}
          />
          {/* 
            we should only show key value when the selected
            model has one i.e. it's been created already 
          */}
          {selectedGroupModel.installationKey && (
            <TextField
              inputProps={
                isKeyMasked
                  ? {
                      style: {
                        fontFamily: "text-security-disc",
                      },
                    }
                  : {}
              }
              InputProps={{
                endAdornment: (
                  <>
                    <InputAdornment
                      className={classes.inputIcons}
                      position={"end"}
                    >
                      <IconButton
                        onClick={() => setIsKeyMasked(!isKeyMasked)}
                        disabled={
                          selectedGroupModel.agentCount ===
                          selectedGroupModel.agentSeatCount
                            ? true
                            : false
                        }
                        className={classes.inputIcons}
                      >
                        {isKeyMasked ? <RemoveRedEye /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                    <InputAdornment position={"end"}>
                      <IconButton
                        onClick={handleCopyKeyValue}
                        disabled={
                          selectedGroupModel.agentCount ===
                          selectedGroupModel.agentSeatCount
                            ? true
                            : false
                        }
                        className={classes.inputIcons}
                      >
                        <FileCopyOutlinedIcon />
                      </IconButton>
                    </InputAdornment>
                  </>
                ),
              }}
              label="Key"
              value={selectedGroupModel.installationKey}
              disabled={
                selectedGroupModel.agentCount ===
                selectedGroupModel.agentSeatCount
                  ? true
                  : false
              }
            />
          )}
          {selectedGroupModel.id &&
            installationScript &&
            renderInstallKeyField()}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            variant={"text"}
            onClick={toggleRemoveKeyDialog}
            className={classes.errorTextButton}
          >
            <DeleteIcon /> Remove installation key
          </Button>
          <div>
            <Button
              color={"primary"}
              style={{ marginRight: 20 }}
              onClick={handleCloseModal}
            >
              Cancel
            </Button>
            <Button
              color={"primary"}
              variant={"contained"}
              onClick={handleSubmit}
            >
              {isSaving ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
      <Dialog open={isRemoveKeyDialogOpen} onClose={toggleRemoveKeyDialog}>
        <DialogTitle>Are you sure?</DialogTitle>
        {/* TODO: Add a dynamic number after "all" here*/}
        <DialogContent>
          This will disable all endpoints that use this installation key.
        </DialogContent>
        <DialogActions>
          <Button
            variant={"text"}
            onClick={toggleRemoveKeyDialog}
            color={"primary"}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemoveKey}
            variant={"contained"}
            className={classes.errorContainedButton}
          >
            Remove installation key
          </Button>
        </DialogActions>
      </Dialog>
      <ActivationConfirmationDialog
        wasEmailSuccessful={wasEmailSuccessful}
        open={isActivationConfirmationDialogOpen}
        onClose={toggleActivationConfirmationDialog}
        handleActivateDevicesFeature={handleActivateDevicesFeature}
      />
    </div>
  );
};

InstallationPageView.defaultProps = {
  keys: [],
};

const mapStateToProps = (state) => {
  const orgId = state.location.payload.orgId;
  const currentUser = state.session.settings.user;

  const currentOrg = state.session.settings.userOrgs.find(
    ({ id }) => id === orgId
  );

  const isOrgAChildOrg = isOrganizationChildOfNFR(currentOrg);
  const isOrgAParentOrg =
    currentOrg && currentOrg?.market === 20 && isNil(currentOrg.parentId);

  return {
    orgId,
    currentOrg,
    currentUser,
    isOrgAChildOrg,
    isOrgAParentOrg,
  };
};

const mapDispatchToProps = (dispatch) => ({
  reload: (force) => dispatch(loadPageData(force)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(InstallationPageView);
